import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Loader2, RefreshCw, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlatformAdminOnly } from "@/components/portal/PlatformAdminOnly";
import { PortalApiError } from "@/lib/portalApi";
import { formatDateTime } from "@/lib/dateFormat";
import { scheduleStatusLabel } from "@/lib/scheduleStatus";
import { platformApi, type OrphanedSchedule } from "@/services/api/platform";

export const Route = createFileRoute("/portal/platform/orphaned-schedules")({
  component: OrphanedSchedulesRoute,
});

function OrphanedSchedulesRoute() {
  return (
    <PlatformAdminOnly backTo="/portal/platform" backLabel="Back to platform">
      <OrphanedSchedulesPage />
    </PlatformAdminOnly>
  );
}

/** Truncated first line — a stored run error can be a full exception + traceback. */
function errorSummary(error: string | null): string | null {
  if (!error?.trim()) return null;
  const firstLine = error.split("\n")[0].trim() || error.trim();
  return firstLine.length > 80 ? `${firstLine.slice(0, 80)}…` : firstLine;
}

export function OrphanedSchedulesPage() {
  const { token } = useAuth();

  const [rows, setRows] = useState<OrphanedSchedule[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<OrphanedSchedule | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<OrphanedSchedule | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApi.listOrphanedSchedules(token);
      setRows(res.orphaned);
      setSkippedCount(res.skippedOrganisationIds.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load orphaned schedules");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /** True when the action failed because someone else already handled the
   * schedule (already deleted, already deactivated, or in another tab) — the
   * row should still be dropped rather than left reading "still orphaned". */
  const isAlreadyGone = (e: unknown) => e instanceof PortalApiError && e.status === 404;

  const handleDeactivate = useCallback(async () => {
    if (!token || !deactivateTarget) return;
    const target = deactivateTarget;
    setBusyId(target.scheduleId);
    setDeactivateTarget(null);
    try {
      await platformApi.deactivateSchedule(token, target.scheduleId);
      toast.success(`Deactivated the schedule for ${target.organisationName}.`);
      setRows((prev) => prev.filter((r) => r.scheduleId !== target.scheduleId));
    } catch (e) {
      if (isAlreadyGone(e)) {
        toast.info("Already handled elsewhere — removing it from this list.");
        setRows((prev) => prev.filter((r) => r.scheduleId !== target.scheduleId));
      } else {
        toast.error(e instanceof Error ? e.message : "Failed to deactivate schedule");
      }
    } finally {
      setBusyId(null);
    }
  }, [token, deactivateTarget]);

  const handleDelete = useCallback(async () => {
    if (!token || !deleteTarget) return;
    const target = deleteTarget;
    setBusyId(target.scheduleId);
    setDeleteTarget(null);
    try {
      await platformApi.deleteSchedule(token, target.scheduleId);
      toast.success(`Deleted the schedule for ${target.organisationName}.`);
      setRows((prev) => prev.filter((r) => r.scheduleId !== target.scheduleId));
    } catch (e) {
      if (isAlreadyGone(e)) {
        toast.info("Already handled elsewhere — removing it from this list.");
        setRows((prev) => prev.filter((r) => r.scheduleId !== target.scheduleId));
      } else {
        toast.error(e instanceof Error ? e.message : "Failed to delete schedule");
      }
    } finally {
      setBusyId(null);
    }
  }, [token, deleteTarget]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Orphaned Reporting Schedules
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Schedules whose report was deleted or renamed after the schedule
            was created. They keep firing forever against a slug that no
            longer exists, failing silently.
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
          Refresh
        </Button>
      </div>

      {skippedCount > 0 && (
        <p className="text-muted-foreground text-sm">
          {skippedCount} organisation{skippedCount === 1 ? "" : "s"} could not
          be checked this run — retry later.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Unlink className="h-5 w-5" />
            Orphaned
            {rows.length > 0 && <Badge variant="destructive">{rows.length}</Badge>}
          </CardTitle>
          <CardDescription>
            No &quot;repoint to another report&quot; action exists here — recreate
            the schedule by hand on the replacement report&apos;s own page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="text-destructive flex items-center gap-2 py-8 text-sm">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-sm">
              No orphaned schedules found.
            </p>
          ) : (
            <TooltipProvider delayDuration={150}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Missing report slug</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Last run status</TableHead>
                    <TableHead>Last run error</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const summary = errorSummary(row.lastRunError);
                    const isBusy = busyId === row.scheduleId;
                    return (
                      <TableRow key={row.scheduleId}>
                        <TableCell>{row.organisationName}</TableCell>
                        <TableCell>
                          {row.displayName ?? (
                            <span className="font-mono text-xs">
                              {row.scheduleId || "—"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {row.reportSlug}
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.isActive ? "default" : "secondary"}>
                            {row.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {row.lastRunStatus
                            ? scheduleStatusLabel(row.lastRunStatus)
                            : "—"}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {summary ? (
                            row.lastRunError && row.lastRunError.length > 80 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-default text-sm">
                                    {summary}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md whitespace-pre-wrap">
                                  {row.lastRunError}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-sm">{summary}</span>
                            )
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(row.createdAtUtc)}</TableCell>
                        <TableCell className="space-x-2 text-right">
                          {row.isActive && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy}
                              onClick={() => setDeactivateTarget(row)}
                            >
                              {isBusy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Deactivate"
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={isBusy}
                            onClick={() => setDeleteTarget(row)}
                          >
                            {isBusy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Delete"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget && (
                <>
                  This stops{" "}
                  {deactivateTarget.displayName ?? deactivateTarget.scheduleId} for{" "}
                  {deactivateTarget.organisationName} from running again. Its
                  configuration, cron and recipients are left untouched, so it
                  can be reactivated later once the report is fixed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeactivate();
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  This permanently removes{" "}
                  {deleteTarget.displayName ?? deleteTarget.scheduleId} for{" "}
                  {deleteTarget.organisationName}. This cannot be undone — to
                  get reporting back, the admin will need to create a new
                  schedule on the replacement report&apos;s own page.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
